/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

import { PlatformSchema } from './devices.js';
import { ProjectLayoutSchema } from './layout.js';

/**
 * The template a project's app is built from: `wails-react` (React, Tailwind CSS and shadcn/ui page) for new projects,
 * `wails-app` (plain TypeScript page) for projects made before it existed, which keep it.
 */
export const TemplateNameSchema = z.enum(['wails-app', 'wails-react']);
export type TemplateName = z.infer<typeof TemplateNameSchema>;
export const DEFAULT_TEMPLATE: TemplateName = 'wails-react';

export const ProjectSchema = z.object({
  project_id: z.string().uuid(),
  name: z.string().min(1).max(80),
  /**
   * The runtime the app is built for and sent to, chosen when the project is made and never changed. Null for projects
   * from before runtimes were chosen.
   */
  runtime_id: z.string().uuid().nullable().default(null),
  /** The runtime's platform and camera models when the project was made: what the app is built for. */
  platform: PlatformSchema.default('linux'),
  camera_models: z.array(z.string().min(1)).default([]),
  template: TemplateNameSchema.default('wails-app'),
  /** The design direction the user chose; later requests keep to it. Null until one was chosen. */
  design: z
    .object({
      run_id: z.string().min(1),
      option_id: z.string().min(1),
      direction: z.string(),
      title: z.string(),
      theme: z.string(),
      note: z.string().optional(),
    })
    .nullable()
    .default(null),
  /**
   * How the page is laid out, from the plan the user approved. With fit, a build whose page does not fit its window
   * fails at the "layout" gate. Null for projects from before layouts were planned: their pages are left as they are.
   */
  layout: ProjectLayoutSchema.nullable().default(null),
  /** The newest version of the app: 0 until a build has succeeded. Failed builds do not count. */
  current_version: z.number().int().nonnegative(),
  /** Let the assistant fix errors a device reports without asking first; on unless turned off through the API. */
  auto_fix: z.boolean().default(true),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectRequestSchema = z
  .object({ name: z.string().trim().min(1).max(80), runtime_id: z.string().uuid() })
  .strict();
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

export const ListProjectsResponseSchema = z.object({ projects: z.array(ProjectSchema) });
export type ListProjectsResponse = z.infer<typeof ListProjectsResponseSchema>;

export const UpdateProjectRequestSchema = z.object({ auto_fix: z.boolean() }).strict();
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;
